import { CommonModule } from "@angular/common";
import { Component, DestroyRef, inject } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { combineLatest, Observable, of, shareReplay, switchMap } from "rxjs";
import { AppMenuItem, isAppMenuBranch } from "./app.menu.types";
import { AppMenuitemComponent } from "./app.menuitem";
import { NotificationKind } from "../../../../domain/notification";
import { isAdminRouteData } from "../../../../domain/route-data";
import { OrganizationService } from "../../../../services/organization.service";
import { UserService } from "../../../../services/user.service";
import { AppNavMenuService } from "../service/app-nav-menu.service";

@Component({
  selector: "app-menu",
  standalone: true,
  imports: [CommonModule, AppMenuitemComponent, RouterModule],
  template: `<ul class="layout-menu">
    <ng-container *ngFor="let item of model | async; let i = index">
      <li app-menuitem *ngIf="!item.separator" [item]="item" [index]="i" [root]="true"></li>
      <li *ngIf="item.separator" class="menu-separator"></li>
    </ng-container>
  </ul>`,
})
export class AppMenuComponent {
  public model: Observable<AppMenuItem[]> = of([]);

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private navMenuService = inject(AppNavMenuService);
  private organizationService = inject(OrganizationService);
  private userService = inject(UserService);
  private destroyRef = inject(DestroyRef);

  constructor() {
    // Core observable that gets the menu model based on route, org, and current user
    const navModelOrgAndUser$ = combineLatest([
      this.route.data,
      this.organizationService.activeOrganization$,
      this.userService.getSelf$(),
    ]).pipe(
      takeUntilDestroyed(),
      switchMap(([data, activeOrg, currentUser]) => {
        const adminData = data["admin"];
        if (isAdminRouteData(adminData) && adminData.isAdminArea) {
          return of([this.navMenuService.getAdminNavModel(), activeOrg, currentUser] as const);
        } else {
          if (!activeOrg || !currentUser) {
            return of([null, null, null] as const);
          }

          return of([this.navMenuService.getUserNavModel(activeOrg, currentUser), activeOrg, currentUser] as const);
        }
      }),
      shareReplay({
        bufferSize: 1,
        refCount: true,
      }),
    );

    // Updates the view, and triggers any async loading required
    navModelOrgAndUser$.subscribe(([model]) => {
      if (!model) {
        return;
      }

      this.model = model;
      const prevNav = this.router.getCurrentNavigation()?.previousNavigation;
      if (!prevNav) {
        // Handles first load of the application, if coming from some external source
        // Loads any async branches along the path to our item, and opens the menu to
        // the correct item.
        this.model
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            switchMap((model) => this.navMenuService.loadItemForUrl(model, this.router.url, 0)),
          )
          .subscribe();
      }
    });

    // Update notification count for "all processes" in the menu
    navModelOrgAndUser$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        switchMap(([menuModel, activeOrg]) => {
          if (!activeOrg || !menuModel) {
            return of([null, null] as const);
          }

          return combineLatest([
            menuModel.pipe(takeUntilDestroyed(this.destroyRef)),
            this.userService.getSelfOrgNotifications$(activeOrg.id),
          ] as const);
        }),
      )
      .subscribe(([menuModel, notifications]) => {
        if (menuModel === null || notifications === null) {
          return;
        }

        const transactionNotificationCount = notifications.filter(
          (n) => n.kind === NotificationKind.Transaction,
        ).length;

        const allProcessMenuItem = this.findMenuItemById(menuModel, "allProcesses");
        if (allProcessMenuItem) {
          allProcessMenuItem.notificationCount = transactionNotificationCount;
        }
      });
  }

  // Recursive search through NavMenuItems to find one with the given ID
  private findMenuItemById(items: AppMenuItem[], id: string): AppMenuItem | null {
    for (const item of items) {
      if (item.id === id) {
        return item;
      } else if (isAppMenuBranch(item) && item.items.length > 0) {
        const found = this.findMenuItemById(item.items, id);
        if (found) {
          return found;
        }
      }
    }
    return null;
  }
}
